import { CartLinesUpdateEvent, CartErrorEvent } from 'https://cdn.shopify.com/storefront/standard-events.js';

const HELPER_VERSION = '2026-07-02';

function getRoutes() {
  return window.routes || window.Theme?.routes || {};
}

function getCartUrl() {
  const routes = getRoutes();
  const base = routes.cart_url || '/cart';
  return `${String(base).replace(/\/$/, '')}.js`;
}

function getQuantity(formData) {
  const quantity = Number(formData?.get?.('quantity'));
  return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
}

function getErrorMessage(error) {
  if (!error) return 'Add to cart failed';
  if (typeof error === 'string') return error;
  return error.message || error.description || error.errors || 'Add to cart failed';
}

async function fetchLatestCart() {
  const response = await fetch(getCartUrl(), {
    method: 'GET',
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch cart after add-to-cart: ${response.status}`);
  }

  return response.json();
}

function beginAddToCart({ target, form, formData, source = 'product-form' } = {}) {
  const variantId = formData?.get?.('id');
  if (!target || !variantId) return null;

  const quantity = getQuantity(formData);
  const productId = target.dataset?.productId || form?.dataset?.productId || undefined;
  const sourceId = form?.id || target.id || source;
  const sellingPlanId = formData?.get?.('selling_plan') || undefined;
  const deferredEvent = CartLinesUpdateEvent.createPromise();

  target.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [
        {
          merchandiseId: String(variantId),
          quantity,
        },
      ],
      promise: deferredEvent.promise,
      detail: {
        source,
        sourceId,
        productId,
        sellingPlanId,
      },
    }),
  );

  let settled = false;

  return {
    async resolve(extraDetail = {}) {
      if (settled) return;
      settled = true;

      try {
        const ajaxCart = await fetchLatestCart();
        deferredEvent.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
          detail: {
            items: ajaxCart.items || [],
            source,
            sourceId,
            itemCount: quantity,
            productId,
            sellingPlanId,
            didError: false,
            ...extraDetail,
          },
        });
      } catch (error) {
        deferredEvent.reject(error);
      }
    },

    reject(error) {
      if (settled) return;
      settled = true;

      const message = getErrorMessage(error);
      deferredEvent.resolve({
        cart: null,
        userErrors: [
          {
            code: error?.code || 'INVALID',
            message,
          },
        ],
        detail: {
          source,
          sourceId,
          itemCount: quantity,
          productId,
          sellingPlanId,
          didError: true,
        },
      });

      target.dispatchEvent(
        new CartErrorEvent({
          error: message,
          code: error?.code || 'INVALID',
          detail: {
            source,
            sourceId,
            productId,
            sellingPlanId,
            rawError: error,
          },
        }),
      );
    },
  };
}

window.ScentellStandardCartEvents = {
  version: HELPER_VERSION,
  beginAddToCart,
};
